import React, { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import CostingSummaryFilters from './components/CostingSummaryFilters';
import HierarchicalSummaryTable from './components/HierarchicalSummaryTable';
import CostingDetailTable from './components/CostingDetailTable';
import RoleCostChart from './components/RoleCostChart';
import MobileSummaryCards from './components/MobileSummaryCards';
import MobileDetailCards from './components/MobileDetailCards';
import MobileChartWrapper from './components/MobileChartWrapper';
import useIsMobile from './hooks/useIsMobile';
import useCostingData from './hooks/useCostingData';
import useCostingSummaryTour from './hooks/useCostingSummaryTour';
import { INITIAL_FILTERS } from './utils/costingHelpers';
import './costingSummary.scss';
import './costingSummary-mobile.scss';

const CostingSummary = ({
    preloadedData,
    setPreloadedData,
    dataLoaded,
    setDataLoaded,
    filters: filtersProp,
    setFilters: setFiltersProp,
    onRegisterClearSelections,
    isMobile: isMobileProp
}) => {
    const isMobileHook = useIsMobile();
    const isMobile = isMobileProp !== undefined ? isMobileProp : isMobileHook;
    const [mobileTab, setMobileTab] = useState('summary');

    const dataReady = dataLoaded && preloadedData && preloadedData.length > 0;

    const [internalFilters, setInternalFilters] = useState({ ...INITIAL_FILTERS });
    const filters = filtersProp || internalFilters;
    const setFilters = setFiltersProp || setInternalFilters;

    const [summarySelection, setSummarySelection] = useState(null);
    const [detailSelection, setDetailSelection] = useState(null);
    const [activeTable, setActiveTable] = useState(null);
    const [resetKey, setResetKey] = useState(0);
    const [chartSelection, setChartSelection] = useState(null);
    const [chartPeriodRange, setChartPeriodRange] = useState([null, null]);

    const { error } = useCostingData({ preloadedData, setPreloadedData, dataLoaded, setDataLoaded });

    useEffect(() => {
        onRegisterClearSelections?.(() => {
            setSummarySelection(null);
            setDetailSelection(null);
            setActiveTable(null);
            setChartSelection(null);
            setChartPeriodRange([null, null]);
            setResetKey(prev => prev + 1);
        });
    }, [onRegisterClearSelections]);

    const globalFilteredData = useMemo(() => {
        if (!preloadedData || preloadedData.length === 0) return [];

        return preloadedData.filter(row => {
            if (filters.sites.length > 0) {
                const siteValues = filters.sites.map(s => s.value);
                if (!siteValues.includes(row.site_Name)) return false;
            }
            if (filters.providers.length > 0) {
                const providerValues = filters.providers.map(p => p.value);
                if (!providerValues.includes(row.providerName)) return false;
            }
            if (filters.roles.length > 0) {
                const roleValues = filters.roles.map(r => r.value);
                if (!roleValues.includes(row.roleName)) return false;
            }
            if (filters.weeks.length > 0) {
                const weekValues = filters.weeks.map(w => w.value);
                if (row.weekEnding) {
                    const rowWeek = new Date(row.weekEnding).toISOString().split('T')[0];
                    if (!weekValues.includes(rowWeek)) return false;
                } else {
                    return false;
                }
            }
            if (filters.periods.length > 0) {
                const periodValues = filters.periods.map(p => p.value);
                if (!periodValues.includes(row.periodName)) return false;
            }
            return true;
        });
    }, [preloadedData, filters]);

    const handleSummaryRowSelect = useCallback((selection) => {
        setSummarySelection(selection);
        setActiveTable('left');
        setDetailSelection(null);
        setChartSelection(null);
    }, []);

    const handleDetailRowSelect = useCallback((selection) => {
        setDetailSelection(selection);
        setActiveTable('right');
    }, []);

    const handleChartBarClick = useCallback((selection) => {
        if (chartSelection &&
            chartSelection.group === selection.group &&
            chartSelection.role === selection.role) {
            setChartSelection(null);
            setSummarySelection(null);
            setDetailSelection(null);
            setActiveTable(null);
            return;
        }

        const newSelection = {
            type: 'weekRole',
            weekEnding: selection.weekEnding,
            roleName: selection.role
        };

        setSummarySelection(newSelection);
        setActiveTable('left');
        setChartSelection(selection);
        setDetailSelection(null);
    }, [chartSelection]);

    const handleClearAll = useCallback(() => {
        setFilters({ ...INITIAL_FILTERS });
        setSummarySelection(null);
        setDetailSelection(null);
        setActiveTable(null);
        setChartSelection(null);
        setChartPeriodRange([null, null]);
        setResetKey(prev => prev + 1);
    }, [setFilters]);

    const { startTour } = useCostingSummaryTour({
        dataReady,
        preloadedData,
        setFilters,
        handleSummaryRowSelect,
        handleChartBarClick,
        handleClearAll,
        setLeftSelection: setSummarySelection,
        setRightSelection: setDetailSelection,
        setActiveTable,
        setChartSelection
    });

    if (error) {
        return (
            <div className="costing-summary-container">
                <div className="costing-summary-empty">
                    <div className="empty-text">{error}</div>
                </div>
            </div>
        );
    }

    const tourButton = (
        <button className="tour-help-btn" onClick={startTour} title="Take a guided tour">
            ?
        </button>
    );

    return (
        <div className="costing-summary-container">
            {!isMobile && (
                <CostingSummaryFilters
                    rawData={preloadedData}
                    filters={filters}
                    setFilters={setFilters}
                    summarySelection={summarySelection}
                    detailSelection={detailSelection}
                    onClearAll={handleClearAll}
                    tourButton={tourButton}
                />
            )}

            {isMobile ? (
                <>
                    <div className="mobile-tab-bar">
                        <button
                            className={`mobile-tab ${mobileTab === 'summary' ? 'active' : ''}`}
                            onClick={() => setMobileTab('summary')}
                        >
                            Summary
                        </button>
                        <button
                            className={`mobile-tab ${mobileTab === 'details' ? 'active' : ''}`}
                            onClick={() => setMobileTab('details')}
                        >
                            Details
                            {summarySelection && <span className="mobile-tab-dot" />}
                        </button>
                        <button
                            className={`mobile-tab ${mobileTab === 'chart' ? 'active' : ''}`}
                            onClick={() => setMobileTab('chart')}
                        >
                            Chart
                        </button>
                    </div>

                    <div className="mobile-tab-content">
                        {mobileTab === 'summary' && (
                            <MobileSummaryCards
                                data={globalFilteredData}
                                onSelectionChange={(sel) => { handleSummaryRowSelect(sel); setMobileTab('details'); }}
                                summarySelection={summarySelection}
                            />
                        )}
                        {mobileTab === 'details' && (
                            <MobileDetailCards
                                data={globalFilteredData}
                                summarySelection={summarySelection}
                                detailSelection={detailSelection}
                                onRowSelect={handleDetailRowSelect}
                            />
                        )}
                        {mobileTab === 'chart' && (
                            <MobileChartWrapper>
                                <RoleCostChart
                                    data={globalFilteredData}
                                    onBarClick={handleChartBarClick}
                                    chartSelection={chartSelection}
                                    periodRange={chartPeriodRange}
                                    onPeriodRangeChange={setChartPeriodRange}
                                    dataLoaded={dataLoaded}
                                />
                            </MobileChartWrapper>
                        )}
                    </div>
                </>
            ) : (
                <div className="tables-container">
                    <div className="left-table-wrapper">
                        <HierarchicalSummaryTable
                            key={resetKey}
                            data={globalFilteredData}
                            summarySelection={summarySelection}
                            onSelectionChange={handleSummaryRowSelect}
                            detailSelection={detailSelection}
                            activeTable={activeTable}
                            dataLoaded={dataReady}
                        />
                    </div>

                    <div className="right-table-wrapper">
                        <div className="right-table-container">
                            <CostingDetailTable
                                data={globalFilteredData}
                                summarySelection={summarySelection}
                                detailSelection={detailSelection}
                                onRowSelect={handleDetailRowSelect}
                                activeTable={activeTable}
                                dataLoaded={dataReady}
                            />
                        </div>
                        <div className="chart-container">
                            <RoleCostChart
                                data={globalFilteredData}
                                onBarClick={handleChartBarClick}
                                chartSelection={chartSelection}
                                periodRange={chartPeriodRange}
                                onPeriodRangeChange={setChartPeriodRange}
                                dataLoaded={dataReady}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

CostingSummary.propTypes = {
    preloadedData: PropTypes.array,
    setPreloadedData: PropTypes.func.isRequired,
    dataLoaded: PropTypes.bool,
    setDataLoaded: PropTypes.func.isRequired,
    filters: PropTypes.object,
    setFilters: PropTypes.func,
    onRegisterClearSelections: PropTypes.func,
    isMobile: PropTypes.bool
};

export default CostingSummary;
